import { BehaviorSubject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

/**
 * Wraps one API call with a BehaviorSubject-backed cache so consumers always
 * have a last-known-good value to fall back to instead of a stuck spinner.
 *
 * Concurrent load() calls for the SAME requestKey (i.e. same params - e.g. two
 * calls both fetching "today's" bookings fired in the same tick) collapse into
 * one HTTP call. Calls with a DIFFERENT requestKey (e.g. the user clicked to a
 * different day/date-range before the previous fetch finished) always fire their
 * own request instead of being silently dropped - only true duplicates dedupe.
 *
 * The fetcher is supplied per load() call (not fixed at construction) so the same
 * cached resource can be re-triggered with different request params (date range,
 * filter type, etc.) - e.g. dashboard summary re-fetched for a different tab/date.
 */
export class CachedResource<T> {
  private readonly dataSubject: BehaviorSubject<T | null>;
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<any>(null);
  private readonly inFlightKeys = new Set<string>();

  readonly data$: Observable<T | null>;
  readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();
  readonly error$: Observable<any> = this.errorSubject.asObservable();

  constructor(
    private readonly storageKey: string,
    private readonly persist: boolean = true
  ) {
    this.dataSubject = new BehaviorSubject<T | null>(this.readCache());
    this.data$ = this.dataSubject.asObservable();
  }

  get snapshot(): T | null {
    return this.dataSubject.getValue();
  }

  load(fetcher: () => Observable<any>, requestKey: string = '', force: boolean = false): void {
    if (this.inFlightKeys.has(requestKey) && !force) return;
    this.inFlightKeys.add(requestKey);
    this.loadingSubject.next(true);
    fetcher().pipe(
      finalize(() => {
        this.inFlightKeys.delete(requestKey);
        if (this.inFlightKeys.size === 0) this.loadingSubject.next(false);
      })
    ).subscribe({
      next: (res: any) => {
        this.dataSubject.next(res);
        this.errorSubject.next(null);
        if (this.persist) this.writeCache(res);
      },
      error: (err: any) => {
        // data$ is intentionally left untouched here so the UI keeps showing
        // the last-known-good value instead of hanging on a spinner forever.
        this.errorSubject.next(err || true);
      }
    });
  }

  clear(): void {
    this.dataSubject.next(null);
    if (this.persist) {
      try { localStorage.removeItem(this.storageKey); } catch { }
    }
  }

  private readCache(): T | null {
    if (!this.persist) return null;
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private writeCache(value: T): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(value));
    } catch { }
  }
}
